import React from 'react';

const Introduction = () => {
  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-white shadow-lg rounded-lg p-8 mb-8">
        <h1 className="text-4xl font-bold text-gray-900 mb-6">
          CyberFundamentals (CyFun) Framework
        </h1>
        <p className="text-xl text-gray-600 mb-8">
          A comprehensive risk assessment framework for determining appropriate cybersecurity assurance levels under the NIS2 directive
        </p>

        <div className="prose prose-lg max-w-none">
          <h2 className="text-2xl font-semibold text-gray-800 mb-4">What is the CyFun Framework?</h2>
          <p className="text-gray-700 mb-6">
            The CyberFundamentals (CyFun) Framework is a risk assessment tool developed by the Centre for Cybersecurity Belgium (CCB). 
            It provides organizations with an easy-to-use method for selecting appropriate cybersecurity assurance levels, inspired by the EU's NIS2 directive.
          </p>

          <h2 className="text-2xl font-semibold text-gray-800 mb-4">Purpose and Background</h2>
          <p className="text-gray-700 mb-6">
            The framework was created to help organizations assess and improve their cybersecurity preparedness by conducting generic risk assessments across 17 different sectors. 
            It focuses on evaluating the national and societal consequences of cyber attacks to determine the most appropriate level of cybersecurity measures.
          </p>

          <h2 className="text-2xl font-semibold text-gray-800 mb-4">NIS2 Directive Compliance</h2>
          <p className="text-gray-700 mb-6">
            The NIS2 directive establishes measures for a high common level of cybersecurity across the European Union. 
            The CyFun framework aligns with these requirements by providing a structured approach to cybersecurity risk assessment and management.
          </p>

          <h2 className="text-2xl font-semibold text-gray-800 mb-4">Three Assurance Levels</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="bg-green-50 border border-green-200 rounded-lg p-6">
              <div className="flex items-center mb-3">
                <div className="w-4 h-4 bg-green-500 rounded-full mr-3"></div>
                <h3 className="text-lg font-semibold text-green-800">BASIC (0-99)</h3>
              </div>
              <p className="text-green-700">
                Fundamental cybersecurity measures suitable for organizations with lower risk profiles. 
                Includes essential protections and basic security controls.
              </p>
            </div>

            <div className="bg-orange-50 border border-orange-200 rounded-lg p-6">
              <div className="flex items-center mb-3">
                <div className="w-4 h-4 bg-orange-500 rounded-full mr-3"></div>
                <h3 className="text-lg font-semibold text-orange-800">IMPORTANT (100-199)</h3>
              </div>
              <p className="text-orange-700">
                Enhanced cybersecurity measures for organizations with moderate risk exposure. 
                Includes additional security controls and monitoring capabilities.
              </p>
            </div>

            <div className="bg-red-50 border border-red-200 rounded-lg p-6">
              <div className="flex items-center mb-3">
                <div className="w-4 h-4 bg-red-500 rounded-full mr-3"></div>
                <h3 className="text-lg font-semibold text-red-800">ESSENTIAL (200+)</h3>
              </div>
              <p className="text-red-700">
                Comprehensive cybersecurity measures for organizations with high risk profiles. 
                Includes advanced security controls and continuous monitoring.
              </p>
            </div>
          </div>

          <h2 className="text-2xl font-semibold text-gray-800 mb-4">Risk Assessment Components</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-blue-800 mb-3">Cyber Attack Categories</h3>
              <ul className="text-blue-700 space-y-2">
                <li>• <strong>Sabotage/Disruption:</strong> DDoS attacks, system disruption</li>
                <li>• <strong>Information Theft:</strong> Espionage, data breaches</li>
                <li>• <strong>Crime:</strong> Ransomware, financial fraud</li>
                <li>• <strong>Hacktivism:</strong> Subversion, website defacement</li>
                <li>• <strong>Disinformation:</strong> Political influence, fake news</li>
              </ul>
            </div>

            <div className="bg-purple-50 border border-purple-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-purple-800 mb-3">Threat Actor Types</h3>
              <ul className="text-purple-700 space-y-2">
                <li>• <strong>Competitors:</strong> Business rivals, industrial espionage</li>
                <li>• <strong>Ideologues:</strong> Political or social activists</li>
                <li>• <strong>Terrorists:</strong> Organized terrorist groups</li>
                <li>• <strong>Cyber Criminals:</strong> Financially motivated attackers</li>
                <li>• <strong>Nation State Actors:</strong> State-sponsored attacks</li>
              </ul>
            </div>
          </div>

          <h2 className="text-2xl font-semibold text-gray-800 mb-4">Sector Coverage</h2>
          <p className="text-gray-700 mb-4">
            The framework provides tailored risk assessments for 17 different sectors, including:
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-gray-50 p-3 rounded-lg text-center">
              <div className="text-2xl mb-2">⚡</div>
              <div className="text-sm font-medium">Energy</div>
            </div>
            <div className="bg-gray-50 p-3 rounded-lg text-center">
              <div className="text-2xl mb-2">🚗</div>
              <div className="text-sm font-medium">Transport</div>
            </div>
            <div className="bg-gray-50 p-3 rounded-lg text-center">
              <div className="text-2xl mb-2">🏦</div>
              <div className="text-sm font-medium">Finance</div>
            </div>
            <div className="bg-gray-50 p-3 rounded-lg text-center">
              <div className="text-2xl mb-2">🏥</div>
              <div className="text-sm font-medium">Healthcare</div>
            </div>
            <div className="bg-gray-50 p-3 rounded-lg text-center">
              <div className="text-2xl mb-2">💧</div>
              <div className="text-sm font-medium">Water</div>
            </div>
            <div className="bg-gray-50 p-3 rounded-lg text-center">
              <div className="text-2xl mb-2">💻</div>
              <div className="text-sm font-medium">ICT</div>
            </div>
            <div className="bg-gray-50 p-3 rounded-lg text-center">
              <div className="text-2xl mb-2">🍎</div>
              <div className="text-sm font-medium">Food</div>
            </div>
            <div className="bg-gray-50 p-3 rounded-lg text-center">
              <div className="text-2xl mb-2">🏭</div>
              <div className="text-sm font-medium">Manufacturing</div>
            </div>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-yellow-800 mb-3">Important Note</h3>
            <p className="text-yellow-700">
              If your organization operates in multiple sectors, the highest assurance level determined 
              across all applicable sectors should be used. This ensures comprehensive protection 
              across all areas of operation.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Introduction;